import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import EmployeePageShell from "./EmployeePageShell";
import AdminPageShell from "../Admin/AdminPageShell";
import useFetch from "../../useFetch";
import { getAuth } from "../../auth";
import { apiRequest } from "../../utils/api";
import FormFeedback, { FieldError, inputErrorClass } from "../Common/FormFeedback";

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch (error) {
    return null;
  }
};

const EmployeePostJob = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const formTopRef = useRef(null);
  const { token } = getAuth();
  const storedUser = getStoredUser();
  const isEmployer = token && storedUser?.role === "employer";
  const isAdmin = token && storedUser?.role === "admin";
  const canPostJob = isEmployer || isAdmin;
  const PageShell = isAdmin ? AdminPageShell : EmployeePageShell;

  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  const [title, setTitle] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyWebsite, setCompanyWebsite] = useState("");
  const [jobType, setJobType] = useState("Full Time");
  const [workplaceType, setWorkplaceType] = useState("On-site");
  const [category, setCategory] = useState("");
  const [location, setLocation] = useState("");
  const [salaryRange, setSalaryRange] = useState("");
  const [salaryMin, setSalaryMin] = useState("");
  const [salaryMax, setSalaryMax] = useState("");
  const [salaryCurrency, setSalaryCurrency] = useState("USD");
  const [salaryPeriod, setSalaryPeriod] = useState("Yearly");
  const [experience, setExperience] = useState("");
  const [qualification, setQualification] = useState("");
  const [careerLevel, setCareerLevel] = useState("");
  const [vacancies, setVacancies] = useState("");
  const [description, setDescription] = useState("");
  const [responsibilities, setResponsibilities] = useState("");
  const [requirements, setRequirements] = useState("");
  const [benefits, setBenefits] = useState("");
  const [skills, setSkills] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [applicationUrl, setApplicationUrl] = useState("");
  const [applicationDeadline, setApplicationDeadline] = useState("");
  const [isUrgent, setIsUrgent] = useState(false);

  const { response: editResponse, isLoading: isLoadingEdit, error: editFetchError } = useFetch(
    isEdit ? process.env.REACT_APP_BASE_API_URL + "/jobs/" + id : null
  );

  const resetForm = () => {
    setTitle("");
    setCompanyName("");
    setCompanyWebsite("");
    setJobType("Full Time");
    setWorkplaceType("On-site");
    setCategory("");
    setLocation("");
    setSalaryRange("");
    setSalaryMin("");
    setSalaryMax("");
    setSalaryCurrency("USD");
    setSalaryPeriod("Yearly");
    setExperience("");
    setQualification("");
    setCareerLevel("");
    setVacancies("");
    setDescription("");
    setResponsibilities("");
    setRequirements("");
    setBenefits("");
    setSkills("");
    setContactEmail("");
    setApplicationUrl("");
    setApplicationDeadline("");
    setIsUrgent(false);
  };

  useEffect(() => {
    if (!editResponse?.job) return;

    const job = editResponse.job;

    setTitle(job.title ?? "");
    setCompanyName(job.company_name ?? "");
    setCompanyWebsite(job.company_website ?? "");
    setJobType(job.job_type ?? "Full Time");
    setWorkplaceType(job.workplace_type ?? "On-site");
    setCategory(job.category ?? "");
    setLocation(job.location ?? "");
    setSalaryRange(job.salary_range ?? "");
    setSalaryMin(job.salary_min ?? "");
    setSalaryMax(job.salary_max ?? "");
    setSalaryCurrency(job.salary_currency ?? "USD");
    setSalaryPeriod(job.salary_period ?? "Yearly");
    setExperience(job.experience ?? "");
    setQualification(job.qualification ?? "");
    setCareerLevel(job.career_level ?? "");
    setVacancies(job.vacancies ?? "");
    setDescription(job.description ?? "");
    setResponsibilities(job.responsibilities ?? "");
    setRequirements(job.requirements ?? "");
    setBenefits(job.benefits ?? "");
    setSkills(Array.isArray(job.skills) ? job.skills.join(", ") : "");
    setContactEmail(job.contact_email ?? "");
    setApplicationUrl(job.application_url ?? "");
    setApplicationDeadline(job.application_deadline ? job.application_deadline.substring(0, 10) : "");
    setIsUrgent(Boolean(job.is_urgent));
  }, [editResponse]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!token) {
      setError("Please login as an employer to post jobs.");
      formTopRef.current?.scrollIntoView();
      return;
    }

    if (!canPostJob) {
      setError("Only employer and admin accounts can post jobs.");
      formTopRef.current?.scrollIntoView();
      return;
    }

    setIsLoading(true);
    setSuccess(false);
    setError(false);
    setValidationErrors([]);
    setFieldErrors({});

    let formData = new FormData();
    formData.append("title", title);
    formData.append("company_name", companyName);
    formData.append("company_website", companyWebsite);
    formData.append("job_type", jobType);
    formData.append("workplace_type", workplaceType);
    formData.append("category", category);
    formData.append("location", location);
    formData.append("salary_range", salaryRange);
    formData.append("salary_min", salaryMin);
    formData.append("salary_max", salaryMax);
    formData.append("salary_currency", salaryCurrency);
    formData.append("salary_period", salaryPeriod);
    formData.append("experience", experience);
    formData.append("qualification", qualification);
    formData.append("career_level", careerLevel);
    formData.append("vacancies", vacancies);
    formData.append("description", description);
    formData.append("responsibilities", responsibilities);
    formData.append("requirements", requirements);
    formData.append("benefits", benefits);
    formData.append("skills", skills);
    formData.append("contact_email", contactEmail);
    if (applicationUrl) {
      formData.append("application_url", applicationUrl);
    }
    formData.append("application_deadline", applicationDeadline);
    formData.append("is_urgent", isUrgent ? "1" : "0");

    apiRequest(isEdit ? "/jobs/" + id + "/update" : "/jobs", {
      method: "POST",
      body: formData,
    })
      .then(data => {
        formTopRef.current?.scrollIntoView();
        setIsLoading(false);

        if (data?.status === "success") {
          setSuccess(data.message);
          if (!isEdit) resetForm();
        }
        else if (data?.status === "validation_error") {
          setValidationErrors(data.messages);
          setFieldErrors(data.errors ?? {});
        }
        else if (data?.status === "error" && data?.message) {
          setError(data.message);
        }
        else {
          setError("There was some error while posting job, please try again.");
        }
      })
      .catch(error => {
        formTopRef.current?.scrollIntoView();
        setIsLoading(false);
        if (error.data?.status === "validation_error") {
          setValidationErrors(error.messages);
          setFieldErrors(error.errors);
        }
        else {
          setError(error.message);
        }
      });
  };

  return (
    <PageShell
      active="post-job"
      title={isEdit ? "Edit Job" : "Post a Job"}
      text={isEdit ? "Update the job details candidates will see." : isAdmin ? "Create a job post directly from the admin panel." : "Create a focused job post. Approved listings expire automatically after 30 days."}
    >
      <div className="ls-widget employee-form-widget">
        <div ref={formTopRef}></div>
        <div className="widget-title">
          <h4>Job Details</h4>
        </div>
        <div className="widget-content">
          {!token && (
            <div className="alert alert-danger">
              <strong>Please login as an employer or admin to post jobs.</strong>{" "}
              <Link to="/login">Login</Link>
            </div>
          )}

          {token && !canPostJob && (
            <div className="alert alert-danger">
              <strong>Only employer and admin accounts can post jobs.</strong>
            </div>
          )}

          <FormFeedback success={success} error={error} validationErrors={validationErrors} />

          {isLoadingEdit && <h5 className="text-center my-3">Loading Data</h5>}

          {editFetchError && (
            <div className="alert alert-danger">
              <strong>{editFetchError}</strong>
            </div>
          )}

          {canPostJob && (
          <form className="default-form" onSubmit={handleSubmit}>
            <div className="row">
              <div className="form-group col-lg-6 col-md-12">
                <label>Job Title</label>
                <input
                  className={inputErrorClass(fieldErrors, "title")}
                  type="text"
                  placeholder="Frontend Developer"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
                <FieldError errors={fieldErrors} name="title" />
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Company Name</label>
                <input
                  className={inputErrorClass(fieldErrors, "location")}
                  type="text"
                  placeholder="Epic Jobs"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
                <FieldError errors={fieldErrors} name="location" />
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Job Type</label>
                <select value={jobType} onChange={(e) => setJobType(e.target.value)} required>
                  <option>Full Time</option>
                  <option>Part Time</option>
                  <option>Contract</option>
                  <option>Remote</option>
                  <option>Internship</option>
                  <option>Temporary</option>
                </select>
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Workplace Type</label>
                <select value={workplaceType} onChange={(e) => setWorkplaceType(e.target.value)}>
                  <option>On-site</option>
                  <option>Hybrid</option>
                  <option>Remote</option>
                </select>
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Category</label>
                <input
                  type="text"
                  placeholder="Software Development"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                />
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Location</label>
                <input
                  type="text"
                  placeholder="Remote"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  required
                />
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Salary Range</label>
                <input
                  type="text"
                  placeholder="$55k - $75k"
                  value={salaryRange}
                  onChange={(e) => setSalaryRange(e.target.value)}
                />
              </div>
              <div className="form-group col-lg-3 col-md-6">
                <label>Salary Min</label>
                <input
                  className={inputErrorClass(fieldErrors, "salary_min")}
                  type="number"
                  placeholder="55000"
                  value={salaryMin}
                  onChange={(e) => setSalaryMin(e.target.value)}
                />
                <FieldError errors={fieldErrors} name="salary_min" />
              </div>
              <div className="form-group col-lg-3 col-md-6">
                <label>Salary Max</label>
                <input
                  className={inputErrorClass(fieldErrors, "salary_max")}
                  type="number"
                  placeholder="75000"
                  value={salaryMax}
                  onChange={(e) => setSalaryMax(e.target.value)}
                />
                <FieldError errors={fieldErrors} name="salary_max" />
              </div>
              <div className="form-group col-lg-3 col-md-6">
                <label>Currency</label>
                <select value={salaryCurrency} onChange={(e) => setSalaryCurrency(e.target.value)}>
                  <option>USD</option>
                  <option>GBP</option>
                  <option>EUR</option>
                  <option>CAD</option>
                  <option>AUD</option>
                  <option>PKR</option>
                </select>
              </div>
              <div className="form-group col-lg-3 col-md-6">
                <label>Salary Period</label>
                <select value={salaryPeriod} onChange={(e) => setSalaryPeriod(e.target.value)}>
                  <option>Hourly</option>
                  <option>Daily</option>
                  <option>Weekly</option>
                  <option>Monthly</option>
                  <option>Yearly</option>
                </select>
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Experience</label>
                <input
                  type="text"
                  placeholder="3+ years"
                  value={experience}
                  onChange={(e) => setExperience(e.target.value)}
                />
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Qualification</label>
                <input
                  type="text"
                  placeholder="Bachelor's degree"
                  value={qualification}
                  onChange={(e) => setQualification(e.target.value)}
                />
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Career Level</label>
                <input
                  type="text"
                  placeholder="Mid-level"
                  value={careerLevel}
                  onChange={(e) => setCareerLevel(e.target.value)}
                />
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Vacancies</label>
                <input
                  type="number"
                  placeholder="2"
                  value={vacancies}
                  onChange={(e) => setVacancies(e.target.value)}
                />
              </div>
              <div className="form-group col-lg-12">
                <label>Job Description</label>
                <textarea
                  className={inputErrorClass(fieldErrors, "description")}
                  placeholder="Describe responsibilities, requirements, and benefits."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                ></textarea>
                <FieldError errors={fieldErrors} name="description" />
              </div>
              <div className="form-group col-lg-12">
                <label>Responsibilities</label>
                <textarea
                  placeholder="Write each responsibility on a new line."
                  value={responsibilities}
                  onChange={(e) => setResponsibilities(e.target.value)}
                ></textarea>
              </div>
              <div className="form-group col-lg-12">
                <label>Requirements</label>
                <textarea
                  placeholder="Write each requirement on a new line."
                  value={requirements}
                  onChange={(e) => setRequirements(e.target.value)}
                ></textarea>
              </div>
              <div className="form-group col-lg-12">
                <label>Benefits</label>
                <textarea
                  placeholder="Healthcare, remote flexibility, learning budget..."
                  value={benefits}
                  onChange={(e) => setBenefits(e.target.value)}
                ></textarea>
              </div>
              <div className="form-group col-lg-12">
                <label>Skills</label>
                <input
                  type="text"
                  placeholder="React, JavaScript, CSS"
                  value={skills}
                  onChange={(e) => setSkills(e.target.value)}
                />
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Contact Email</label>
                <input
                  className={inputErrorClass(fieldErrors, "contact_email")}
                  type="email"
                  placeholder="hr@company.com"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
                <FieldError errors={fieldErrors} name="contact_email" />
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Application Deadline</label>
                <input
                  className={inputErrorClass(fieldErrors, "application_deadline")}
                  type="date"
                  value={applicationDeadline}
                  onChange={(e) => setApplicationDeadline(e.target.value)}
                />
                <FieldError errors={fieldErrors} name="application_deadline" />
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Company Website</label>
                <input
                  className={inputErrorClass(fieldErrors, "company_website")}
                  type="text"
                  placeholder="www.company.com or none"
                  value={companyWebsite}
                  onChange={(e) => setCompanyWebsite(e.target.value)}
                />
                <FieldError errors={fieldErrors} name="company_website" />
              </div>
              <div className="form-group col-lg-12">
                <div className="field-outer">
                  <div className="input-group checkboxes square">
                    <input
                      id="is-urgent"
                      type="checkbox"
                      checked={isUrgent}
                      onChange={(e) => setIsUrgent(e.target.checked)}
                    />
                    <label htmlFor="is-urgent" className="remember">
                      <span className="custom-checkbox"></span> Mark this job as urgent
                    </label>
                  </div>
                </div>
              </div>
              <div className="form-group col-lg-12">
                <button type="submit" className="theme-btn btn-style-one" disabled={isLoading}>
                  {isLoading ? "Loading..." : isEdit ? "Update Job" : "Publish Job"}
                </button>
                <Link to={isEdit ? "/employee-manage-jobs" : "/employee-job-preview"} className="theme-btn btn-style-three" style={{ marginLeft: 12 }}>
                  {isEdit ? "Back to Jobs" : "Preview"}
                </Link>
              </div>
            </div>
          </form>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default EmployeePostJob;
